import { useEffect, useState } from "react";
import { firms as firmsService } from "../../../api/firms.service";
import { editorialCategories as editorialCategoriesService } from "../../../api/editorialCategories.service";
import type { Subscription } from "../../../schemas/subscriptionSchema";
import type { SubscriptionWithFeatures } from "../../../api/subscriptions.service";

type SelectOption = {
  value: string | number;
  label: string;
};

/**
 * Fetches initial option labels for async select fields in edit mode.
 * These are needed to display the selected values before the user searches.
 */
export function useInitialFormOptions(
  subscription: Subscription | SubscriptionWithFeatures | null,
) {
  const [initialFirmOption, setInitialFirmOption] = useState<SelectOption | null>(null);
  const [initialRegionOption, setInitialRegionOption] = useState<SelectOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchInitialOptions = async () => {
      if (!subscription) return;

      setIsLoading(true);
      const subWithFeatures = subscription as SubscriptionWithFeatures;

      // Fetch firm name - use companyName from BFF enrichment if available
      if (subscription.firmId) {
        if (subscription.companyName) {
          setInitialFirmOption({
            value: subscription.firmId,
            label: `${subscription.companyName} (ID: ${subscription.firmId})`,
          });
        } else {
          try {
            const firm = await firmsService.getById(Number(subscription.firmId));
            setInitialFirmOption({
              value: subscription.firmId,
              label: `${firm?.name ?? "Company"} (ID: ${subscription.firmId})`,
            });
          } catch {
            setInitialFirmOption({
              value: subscription.firmId,
              label: `Company (ID: ${subscription.firmId})`,
            });
          }
        }
      }

      // Fetch placement region name from editorial categories API
      if (subscription.siteStructureId) {
        if (subWithFeatures.siteStructure?.name) {
          setInitialRegionOption({
            value: subscription.siteStructureId,
            label: subWithFeatures.siteStructure.name,
          });
        } else {
          const numericId = Number(subscription.siteStructureId);
          if (Number.isNaN(numericId)) {
            setInitialRegionOption({
              value: subscription.siteStructureId,
              label: String(subscription.siteStructureId),
            });
          } else {
            try {
              const category = await editorialCategoriesService.getById(numericId);
              setInitialRegionOption({
                value: subscription.siteStructureId,
                label: category?.country ?? `Region ${subscription.siteStructureId}`,
              });
            } catch {
              setInitialRegionOption({
                value: subscription.siteStructureId,
                label: `Region ${subscription.siteStructureId}`,
              });
            }
          }
        }
      }

      setIsLoading(false);
    };

    fetchInitialOptions();
  }, [subscription]);

  return { initialFirmOption, initialRegionOption, isLoading };
}
